import { Navigate, NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, CalendarCheck, Wrench, LogOut, Sparkles, CalendarRange, Images, Repeat, PhoneCall } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const navItems = [
  { to: "/admin", end: true, label: "Overview", icon: LayoutDashboard, testid: "admin-nav-overview" },
  { to: "/admin/bookings", label: "Bookings", icon: CalendarCheck, testid: "admin-nav-bookings" },
  { to: "/admin/calendar", label: "Calendar", icon: CalendarRange, testid: "admin-nav-calendar" },
  { to: "/admin/callbacks", label: "Callbacks", icon: PhoneCall, testid: "admin-nav-callbacks" },
  { to: "/admin/services", label: "Services", icon: Wrench, testid: "admin-nav-services" },
  { to: "/admin/gallery", label: "Gallery", icon: Images, testid: "admin-nav-gallery" },
  { to: "/admin/subscriptions", label: "Plans", icon: Repeat, testid: "admin-nav-subscriptions" },
];

export default function AdminLayout() {
  const { admin, logout } = useAuth();
  const navigate = useNavigate();

  if (admin === undefined) {
    return (
      <div className="min-h-screen bg-[#0b0f17] flex items-center justify-center text-slate-400">
        Loading…
      </div>
    );
  }
  if (!admin) return <Navigate to="/admin/login" replace />;

  return (
    <div className="min-h-screen bg-[#0b0f17] text-slate-200 flex flex-col lg:flex-row">
      {/* Sidebar */}
      <aside className="lg:w-64 lg:border-r border-b lg:border-b-0 border-white/10 bg-[#0a0e15]" data-testid="admin-sidebar">
        <div className="p-5 flex items-center gap-2">
          <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-[#ff6b00] to-[#ff8a2a] flex items-center justify-center">
            <Sparkles className="h-5 w-5 text-[#0b0f17]" strokeWidth={2.6} />
          </div>
          <div>
            <div className="font-display uppercase font-extrabold text-sm text-slate-100 leading-tight">
              CJ's CRM
            </div>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
              Admin Portal
            </div>
          </div>
        </div>
        <nav className="px-3 py-3 flex lg:flex-col gap-1 overflow-x-auto lg:overflow-visible">
          {navItems.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={n.testid}
              className={({ isActive }) =>
                `flex items-center gap-2 px-3 py-2.5 rounded-lg font-display uppercase font-semibold text-sm tracking-wider whitespace-nowrap transition ${
                  isActive
                    ? "bg-[#ff6b00]/10 text-[#ff6b00]"
                    : "text-slate-400 hover:text-slate-100 hover:bg-white/5"
                }`
              }
            >
              <n.icon className="h-4 w-4" />
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="hidden lg:block mt-auto p-3">
          <div className="rounded-xl bg-[#121824] border border-white/10 p-3">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
              Signed in as
            </div>
            <div className="text-sm text-slate-200 truncate mt-1">{admin.email}</div>
            <button
              onClick={async () => {
                await logout();
                navigate("/admin/login");
              }}
              data-testid="admin-logout-button"
              className="cj-btn-ghost mt-3 w-full px-3 py-2 rounded-lg text-xs inline-flex items-center justify-center gap-2"
            >
              <LogOut className="h-3.5 w-3.5" /> Logout
            </button>
          </div>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0 p-5 sm:p-8">
        <Outlet />
      </main>
    </div>
  );
}
